import { createElement, createContext, useContext, useState, useEffect } from 'react';
import type { FontAvailabilityContextType, FontAvailabilityProviderProps } from './types';

const FontAvailabilityContext = createContext<FontAvailabilityContextType>({
  newFontLoaded: false,
});

export const useFontAvailabilityContext = () => {
  return useContext(FontAvailabilityContext);
};

/**
 * The FontAvailabilityProvider will update whenever a document.fonts 'loadingdone' event listener is triggered.
 * For the time being we're only concerned with knowing that a new font has finished loading, information about
 * the font isn't necessary. To invalidate text measurements use the custom hook useTextMeasurementInvalidator.
 */
export const FontAvailabilityProvider = (props: FontAvailabilityProviderProps) => {
  // Use an object for the state so the component rerenders when the state is set, even if the value is the same.
  const [fontLoaded, setFontLoaded] = useState<FontAvailabilityContextType>({
    newFontLoaded: false,
  });

  useEffect(() => {
    if (!document.fonts) return;
    const loadingdoneHandler = () => {
      setFontLoaded({ newFontLoaded: true });
    };
    document.fonts.addEventListener('loadingdone', loadingdoneHandler);
    return () => document.fonts.removeEventListener('loadingdone', loadingdoneHandler);
  }, []);

  return (
    <FontAvailabilityContext.Provider value={fontLoaded}>
      {props.children}
    </FontAvailabilityContext.Provider>
  );
};
